import PageHeader from "@/components/layout/PageHeader";
import WhyUs from "@/components/sections/WhyUs";
import Testimonials from "@/components/sections/Testimonials";
import { IMAGES, BRAND, STATS } from "@/data/site";
import { Check, ShieldCheck, Award, Users } from "lucide-react";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="Hakkımızda"
        title="Eskişehir'in güvenilir"
        accent="otogaz & akü adresi."
        subtitle="Star Otogaz olarak 40 yılı aşkın tecrübemizle 10.000'den fazla araca dokunduk. Sertifikalı ekibimiz, modern ekipmanımız ve İnci Akü yetkili bayiliğimiz ile sektörün önde gelen markalarındanız."
      />

      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gold/10 blur-3xl rounded-full" />
            <div className="relative rounded-2xl overflow-hidden glass-gold">
              <img
                src={IMAGES.about}
                alt="Star Otogaz atölyemiz"
                className="w-full h-[520px] object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-5 left-5 right-5 glass-strong rounded-xl p-5">
                <div className="text-gold uppercase tracking-[0.25em] text-[10px] font-semibold mb-1">
                  Atölyemiz
                </div>
                <div className="font-display font-bold text-white text-lg">
                  Tepebaşı / Eskişehir
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="text-gold uppercase tracking-[0.4em] text-xs mb-3">
              Hikayemiz
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight mb-6 text-balance">
              <span className="text-gradient-silver">40 yıllık tecrübe, </span>
              <span className="text-gradient-gold">
                tek bir hedefe odaklı: aracınızı tanıyan profesyonel servis.
              </span>
            </h2>
            <p className="text-white/70 leading-relaxed mb-5">
      Star Otogaz olarak 40 yılı aşkın tecrübemizle Eskişehir Tepebaşı'nda
              LPG dönüşümü ve akü servisi alanında uzmanlaşmış kadromuzla hizmet
              veriyoruz. İnci Akü, Varta ve Bosch marka akülerin satış ve
              montajını gerçekleştiriyor; bilgisayarlı LPG ayarı, sıralı sistem
              servisi, akü test ve montajı, periyodik bakım gibi tüm hizmetleri
              tek çatı altında sunuyoruz.
            </p>
            <p className="text-white/70 leading-relaxed mb-7">
              İnci Akü yetkili bayisi olarak, tüm modellerde orijinal ürün ve
              gerçek garantiyi müşterilerimize ulaştırıyoruz. Her bir araç,
              bizim için bir güven imzasıdır.
            </p>

            <ul className="space-y-3">
              {[
                "Sertifikalı ve deneyimli teknisyen kadrosu",
                "Modern diagnostik ve test ekipmanları",
                "Şeffaf fiyatlandırma ve yazılı garanti",
                "İnci Akü orijinal ürün güvencesi",
                "Aynı gün servis & hızlı çözüm",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-gold/15 border border-gold/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-gold" />
                  </div>
                  <span className="text-white/85">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#050A1F] relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint opacity-25" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-5xl lg:text-6xl font-bold text-gradient-gold mb-2">
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-[0.25em] text-white/60">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <p className="text-gold uppercase tracking-[0.4em] text-xs mb-3">
              Değerlerimiz
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight text-balance">
              <span className="text-gradient-silver">Güven,</span>{" "}
              <span className="text-gradient-gold">
                tecrübe ve şeffaflık.
              </span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: ShieldCheck,
                title: "Güvenilirlik",
                text:
                  "Her uygulama sertifikalı ve sigortalı. Yazılı garanti.",
              },
              {
                icon: Award,
                title: "Tecrübe",
                text:
                  "15+ yıl deneyim, on binlerce başarılı serviste sınanmış uzmanlık.",
              },
              {
                icon: Users,
                title: "Müşteri Odaklılık",
                text:
                  "Açık iletişim, dürüst fiyat ve takip eden müşteri ilişkileri.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="glass rounded-2xl p-7 card-lift hover:border-gold/50"
              >
                <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-2">
                  {title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <WhyUs />
      <Testimonials />
    </div>
  );
}
